// src/dom/style-registry.ts
var DEFAULT_THEME_BY_PRESET = {
  classic: {
    baseColor: "#e8ecf1",
    highlightColor: "#f7f9fb",
    durationMs: 1100,
    easing: "linear"
  },
  cool: {
    baseColor: "#dde6f7",
    highlightColor: "#f4f8ff",
    durationMs: 1200,
    easing: "ease-in-out"
  },
  warm: {
    baseColor: "#efe5d6",
    highlightColor: "#fff8ee",
    durationMs: 1250,
    easing: "ease-in-out"
  },
  contrast: {
    baseColor: "#ced4dc",
    highlightColor: "#f8fbff",
    durationMs: 980,
    easing: "linear"
  }
};
var resolveThemeTokens = (theme) => {
  if (typeof theme === "string") {
    return DEFAULT_THEME_BY_PRESET[theme];
  }
  return {
    baseColor: theme.baseColor,
    highlightColor: theme.highlightColor,
    durationMs: theme.durationMs ?? 1100,
    easing: theme.easing ?? "linear"
  };
};
var overlayBackground = (baseColor, highlightColor) => {
  return `linear-gradient(90deg, ${baseColor} 18%, ${highlightColor} 50%, ${baseColor} 82%)`;
};
var toAnimationFrames = (preset) => {
  if (preset === "none") {
    return "0% { opacity: 1; } 100% { opacity: 1; }";
  }
  if (preset === "wave") {
    return "0% { transform: translateX(-12%); } 50% { transform: translateX(0%); } 100% { transform: translateX(12%); }";
  }
  if (preset === "pulse") {
    return "0% { opacity: 0.62; } 50% { opacity: 1; } 100% { opacity: 0.62; }";
  }
  if (preset === "breathe") {
    return "0% { opacity: 0.78; filter: saturate(0.94); } 50% { opacity: 1; filter: saturate(1.08); } 100% { opacity: 0.78; filter: saturate(0.94); }";
  }
  return "0% { background-position: 100% 0; } 100% { background-position: 0 0; }";
};
var toAnimationName = (instanceClassName) => {
  return `${instanceClassName.replace(/[^a-zA-Z0-9_-]/g, "")}-anim`;
};
var toAnimationRule = (preset, durationMs, easing, animationName) => {
  if (preset === "none") {
    return "none";
  }
  return `${animationName} ${Math.max(240, durationMs)}ms ${easing} infinite`;
};
var createStyleCss = (instanceClassName, overlayClassName, visuals) => {
  const themeTokens = resolveThemeTokens(visuals.theme);
  const animationName = toAnimationName(instanceClassName);
  const animationRule = toAnimationRule(
    visuals.animation,
    themeTokens.durationMs,
    themeTokens.easing,
    animationName
  );
  return `
.${instanceClassName} {
  --sknet-base: ${themeTokens.baseColor};
  --sknet-highlight: ${themeTokens.highlightColor};
  --sknet-line-gap: ${visuals.adaptive.lineGapPx}px;
  --sknet-min-block-height: ${visuals.adaptive.minBlockHeightPx}px;
}

.${overlayClassName}.${instanceClassName}[data-skeleton-mode="overlay"] {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  border-radius: inherit;
  background: ${overlayBackground(themeTokens.baseColor, themeTokens.highlightColor)};
  background-size: 300% 100%;
  animation: ${animationRule};
}

.${overlayClassName}.${instanceClassName}[data-skeleton-mode="adaptive"] {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  border-radius: inherit;
  background: transparent;
  overflow: hidden;
}

.${overlayClassName}.${instanceClassName}[data-skeleton-mode="adaptive"] .sknet-adaptive-block {
  position: absolute;
  background: ${overlayBackground(themeTokens.baseColor, themeTokens.highlightColor)};
  background-size: 300% 100%;
  animation: ${animationRule};
}

@keyframes ${animationName} {
  ${toAnimationFrames(visuals.animation)}
}

@media (prefers-reduced-motion: reduce) {
  .${overlayClassName}.${instanceClassName}[data-skeleton-mode="overlay"],
  .${overlayClassName}.${instanceClassName}[data-skeleton-mode="adaptive"] .sknet-adaptive-block {
    animation: none;
    background-position: 50% 0;
  }
}
`;
};
var styleCounter = 0;
var SkeletonStyleRegistry = class {
  constructor(overlayClassName, visuals) {
    this.overlayClassName = overlayClassName;
    this.visuals = visuals;
    this.styleElement = null;
    styleCounter += 1;
    this.instanceClassName = `sknet-visual-${styleCounter}`;
  }
  ensureAttached() {
    if (typeof document === "undefined" || this.styleElement) {
      return;
    }
    const style = document.createElement("style");
    style.setAttribute("data-sknet-style", this.instanceClassName);
    style.textContent = createStyleCss(this.instanceClassName, this.overlayClassName, this.visuals);
    document.head.append(style);
    this.styleElement = style;
  }
  detach() {
    this.styleElement?.remove();
    this.styleElement = null;
  }
};

// src/dom/skeleton-manager.ts
var SKELETON_NODE_ID = "sknet-skeleton-node";
var ADAPTIVE_BLOCK_ATTR = "data-skeleton-placeholder";
var ADAPTIVE_BLOCK_VALUE = "block";
var DEFAULT_VISUALS = {
  mode: "hybrid",
  animation: "shimmer",
  theme: "classic",
  adaptive: {
    maxDepth: 4,
    maxPlaceholders: 72,
    minBlockHeightPx: 8,
    lineGapPx: 5,
    ignoreSelectors: [
      "[data-skeleton-node]",
      "script",
      "style",
      "noscript",
      "template",
      "iframe",
      "canvas"
    ]
  }
};
var TEXT_TAGS = /* @__PURE__ */ new Set([
  "p",
  "span",
  "label",
  "small",
  "strong",
  "em",
  "a",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "td",
  "th"
]);
var SOLID_BLOCK_TAGS = /* @__PURE__ */ new Set(["img", "video", "button", "input", "select", "textarea", "svg"]);
var parsePixelValue = (value) => {
  if (!value) {
    return 0;
  }
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return parsed;
};
var normalizeVisuals = (visuals) => {
  return {
    mode: visuals?.mode ?? DEFAULT_VISUALS.mode,
    animation: visuals?.animation ?? DEFAULT_VISUALS.animation,
    theme: visuals?.theme ?? DEFAULT_VISUALS.theme,
    adaptive: {
      maxDepth: visuals?.adaptive?.maxDepth ?? DEFAULT_VISUALS.adaptive.maxDepth,
      maxPlaceholders: visuals?.adaptive?.maxPlaceholders ?? DEFAULT_VISUALS.adaptive.maxPlaceholders,
      minBlockHeightPx: visuals?.adaptive?.minBlockHeightPx ?? DEFAULT_VISUALS.adaptive.minBlockHeightPx,
      lineGapPx: visuals?.adaptive?.lineGapPx ?? DEFAULT_VISUALS.adaptive.lineGapPx,
      ignoreSelectors: visuals?.adaptive?.ignoreSelectors ?? DEFAULT_VISUALS.adaptive.ignoreSelectors
    }
  };
};
var SkeletonManager = class {
  constructor(options) {
    this.options = options;
    this.activeRequestIds = /* @__PURE__ */ new Set();
    this.target = null;
    this.targetPositionPatched = false;
    this.originalTargetPosition = null;
    this.visuals = normalizeVisuals(options.visuals);
    this.styleRegistry = new SkeletonStyleRegistry(options.overlayClassName, this.visuals);
  }
  getTarget() {
    return this.resolveTarget();
  }
  show(requestId) {
    const target = this.resolveTarget();
    if (!target) {
      return;
    }
    this.styleRegistry.ensureAttached();
    this.ensureTargetPosition(target);
    this.activeRequestIds.add(requestId);
    target.setAttribute("data-skeleton-visible", "true");
    target.classList.add(this.options.className);
    this.ensureSkeletonNode(target);
  }
  hide(requestId) {
    const target = this.resolveTarget();
    if (!target) {
      return;
    }
    this.activeRequestIds.delete(requestId);
    if (this.activeRequestIds.size > 0) {
      return;
    }
    target.setAttribute("data-skeleton-visible", "false");
    target.classList.remove(this.options.className);
    target.querySelector(`[data-skeleton-node="${SKELETON_NODE_ID}"]`)?.remove();
    this.restoreTargetPosition(target);
  }
  cleanup() {
    const target = this.resolveTarget();
    if (!target) {
      return;
    }
    this.activeRequestIds.clear();
    target.setAttribute("data-skeleton-visible", "false");
    target.classList.remove(this.options.className);
    target.querySelector(`[data-skeleton-node="${SKELETON_NODE_ID}"]`)?.remove();
    this.restoreTargetPosition(target);
  }
  dispose() {
    this.cleanup();
    this.styleRegistry.detach();
  }
  resolveTarget() {
    if (this.target && this.target.isConnected) {
      return this.target;
    }
    this.target = document.querySelector(this.options.selector);
    return this.target;
  }
  ensureTargetPosition(target) {
    if (this.targetPositionPatched) {
      return;
    }
    const computed = window.getComputedStyle(target);
    if (computed.position !== "static") {
      return;
    }
    this.targetPositionPatched = true;
    this.originalTargetPosition = target.style.position;
    target.style.position = "relative";
    target.setAttribute("data-skeleton-position-patched", "true");
  }
  restoreTargetPosition(target) {
    if (!this.targetPositionPatched) {
      return;
    }
    target.style.position = this.originalTargetPosition ?? "";
    target.removeAttribute("data-skeleton-position-patched");
    this.targetPositionPatched = false;
    this.originalTargetPosition = null;
  }
  ensureSkeletonNode(target) {
    const existing = target.querySelector(`[data-skeleton-node="${SKELETON_NODE_ID}"]`);
    if (existing) {
      return;
    }
    const node = document.createElement("div");
    node.setAttribute("data-skeleton-node", SKELETON_NODE_ID);
    node.setAttribute("aria-hidden", "true");
    node.className = `${this.options.overlayClassName} ${this.styleRegistry.instanceClassName}`;
    const renderMode = this.renderModeForTarget();
    node.setAttribute("data-skeleton-mode", renderMode);
    if (renderMode === "adaptive") {
      let created = false;
      try {
        created = this.renderAdaptivePlaceholders(target, node);
      } catch {
        created = false;
      }
      if (!created) {
        node.setAttribute("data-skeleton-mode", "overlay");
      }
    }
    target.prepend(node);
  }
  renderModeForTarget() {
    if (this.visuals.mode === "overlay") {
      return "overlay";
    }
    if (this.visuals.mode === "adaptive") {
      return "adaptive";
    }
    return "adaptive";
  }
  renderAdaptivePlaceholders(target, node) {
    node.innerHTML = "";
    const candidates = this.collectAdaptiveCandidates(target);
    if (candidates.length === 0) {
      return false;
    }
    const targetRect = target.getBoundingClientRect();
    let blockCount = 0;
    for (const candidate of candidates) {
      if (blockCount >= this.visuals.adaptive.maxPlaceholders) {
        break;
      }
      const blocks = this.toCandidateBlocks(candidate, targetRect, blockCount);
      for (const block of blocks) {
        if (blockCount >= this.visuals.adaptive.maxPlaceholders) {
          break;
        }
        node.append(block);
        blockCount += 1;
      }
    }
    return blockCount > 0;
  }
  collectAdaptiveCandidates(target) {
    const candidates = [];
    const maxDepth = Math.max(1, this.visuals.adaptive.maxDepth);
    const ignoreSelectors = this.visuals.adaptive.ignoreSelectors.map((selector = "") => selector.trim()).filter(Boolean);
    const ignoreSelectorList = ignoreSelectors.join(",");
    const rootRect = target.getBoundingClientRect();
    const walker = document.createTreeWalker(target, NodeFilter.SHOW_ELEMENT);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!(node instanceof HTMLElement)) {
        continue;
      }
      const depth = this.getDepth(target, node);
      if (depth > maxDepth) {
        continue;
      }
      if (ignoreSelectorList) {
        try {
          if (node.matches(ignoreSelectorList)) {
            continue;
          }
        } catch {
        }
      }
      if (node.hasAttribute("data-skeleton-node")) {
        continue;
      }
      const computed = window.getComputedStyle(node);
      if (computed.display === "none" || computed.visibility === "hidden" || computed.opacity === "0") {
        continue;
      }
      const rect = node.getBoundingClientRect();
      if (rect.width < 6 || rect.height < this.visuals.adaptive.minBlockHeightPx) {
        continue;
      }
      if (rect.bottom < rootRect.top || rect.top > rootRect.bottom || rect.right < rootRect.left || rect.left > rootRect.right) {
        continue;
      }
      if (!node.textContent?.trim() && !SOLID_BLOCK_TAGS.has(node.tagName.toLowerCase())) {
        continue;
      }
      candidates.push(node);
    }
    return candidates;
  }
  getDepth(root, node) {
    let depth = 0;
    let current = node;
    while (current && current !== root) {
      current = current.parentElement;
      depth += 1;
      if (depth > 50) {
        break;
      }
    }
    return depth;
  }
  toCandidateBlocks(candidate, targetRect, seed) {
    const tag = candidate.tagName.toLowerCase();
    const rect = candidate.getBoundingClientRect();
    const localLeft = Math.max(0, rect.left - targetRect.left);
    const localTop = Math.max(0, rect.top - targetRect.top);
    const localWidth = Math.max(8, rect.width);
    const localHeight = Math.max(this.visuals.adaptive.minBlockHeightPx, rect.height);
    if (TEXT_TAGS.has(tag)) {
      return this.toTextBlocks(candidate, localLeft, localTop, localWidth, localHeight, seed);
    }
    return [
      this.createBlock({
        left: localLeft,
        top: localTop,
        width: localWidth,
        height: localHeight,
        radius: parsePixelValue(window.getComputedStyle(candidate).borderRadius) || 8
      })
    ];
  }
  toTextBlocks(candidate, left, top, width, height, seed) {
    const computed = window.getComputedStyle(candidate);
    const lineHeight = parsePixelValue(computed.lineHeight) || parsePixelValue(computed.fontSize) * 1.35 || 16;
    const gap = this.visuals.adaptive.lineGapPx;
    const lineCount = Math.max(1, Math.min(4, Math.round(height / Math.max(8, lineHeight + gap))));
    const blocks = [];
    for (let index = 0; index < lineCount; index += 1) {
      const isLast = index === lineCount - 1;
      const widthRatio = isLast ? this.widthRatioBySeed(seed + index) : 0.98;
      const blockWidth = Math.max(14, width * widthRatio);
      const blockTop = top + index * (lineHeight + gap);
      blocks.push(
        this.createBlock({
          left,
          top: blockTop,
          width: blockWidth,
          height: Math.max(this.visuals.adaptive.minBlockHeightPx, lineHeight * 0.82),
          radius: Math.max(4, Math.round(lineHeight * 0.28))
        })
      );
    }
    return blocks;
  }
  widthRatioBySeed(seed) {
    const cycle = [0.62, 0.7, 0.78, 0.84, 0.9];
    return cycle[Math.abs(seed) % cycle.length];
  }
  createBlock(input) {
    const block = document.createElement("div");
    block.className = "sknet-adaptive-block";
    block.setAttribute(ADAPTIVE_BLOCK_ATTR, ADAPTIVE_BLOCK_VALUE);
    block.style.left = `${Math.max(0, input.left)}px`;
    block.style.top = `${Math.max(0, input.top)}px`;
    block.style.width = `${Math.max(4, input.width)}px`;
    block.style.height = `${Math.max(this.visuals.adaptive.minBlockHeightPx, input.height)}px`;
    block.style.borderRadius = `${Math.max(0, input.radius)}px`;
    return block;
  }
};

// src/utils/guards.ts
var isBrowserEnvironment = () => {
  return typeof window !== "undefined" && typeof document !== "undefined";
};
var isFetchAvailable = () => {
  return typeof globalThis.fetch === "function";
};
var isXmlHttpRequestAvailable = () => {
  return typeof globalThis.XMLHttpRequest === "function";
};

// src/network/global-patch-registry.ts
var FETCH_SOURCE = "fetch";
var XHR_SOURCE = "xhr";
var XHR_META = /* @__PURE__ */ Symbol("sknet:xhr-meta");
var subscriberIdCounter = 0;
var fetchSubscribers = /* @__PURE__ */ new Map();
var xhrSubscribers = /* @__PURE__ */ new Map();
var originalFetch = null;
var originalXhrOpen = null;
var originalXhrSend = null;
var fetchRequestCounter = 0;
var xhrRequestCounter = 0;
var activeXhrRequests = /* @__PURE__ */ new Map();
var withSubscribers = (subscribers, invoke) => {
  subscribers.forEach((subscriber) => invoke(subscriber));
};
var toError = (error) => {
  return error instanceof Error ? error : new Error(String(error));
};
var isAbortError = (error) => {
  return error.name === "AbortError";
};
var getAbortReason = (signal) => {
  if ("reason" in signal) {
    return signal.reason;
  }
  return new Error("Aborted");
};
var composeAbortSignals = (left, right) => {
  if (!left && !right) {
    return void 0;
  }
  if (!left) {
    return right ?? void 0;
  }
  if (!right) {
    return left ?? void 0;
  }
  const abortSignalWithAny = AbortSignal;
  if (typeof abortSignalWithAny.any === "function") {
    return abortSignalWithAny.any([left, right]);
  }
  const compositeController = new AbortController();
  const abort = (signal) => {
    if (compositeController.signal.aborted) {
      return;
    }
    compositeController.abort(getAbortReason(signal));
  };
  if (left.aborted) {
    abort(left);
  } else {
    left.addEventListener("abort", () => abort(left), { once: true });
  }
  if (right.aborted) {
    abort(right);
  } else {
    right.addEventListener("abort", () => abort(right), { once: true });
  }
  return compositeController.signal;
};
var resolveFetchMethod = (input, init) => {
  if (init?.method) {
    return init.method.toUpperCase();
  }
  if (typeof Request !== "undefined" && input instanceof Request) {
    return input.method.toUpperCase();
  }
  return "GET";
};
var resolveFetchUrl = (input) => {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.toString();
  }
  return input.url;
};
var emitFetchStart = (event) => {
  withSubscribers(fetchSubscribers, (subscriber) => subscriber.onRequestStart(event));
};
var emitFetchEnd = (event) => {
  withSubscribers(fetchSubscribers, (subscriber) => subscriber.onRequestEnd(event));
};
var emitXhrStart = (event) => {
  withSubscribers(xhrSubscribers, (subscriber) => subscriber.onRequestStart(event));
};
var emitXhrEnd = (event) => {
  withSubscribers(xhrSubscribers, (subscriber) => subscriber.onRequestEnd(event));
};
var ensureFetchPatch = () => {
  if (originalFetch || !isFetchAvailable()) {
    return;
  }
  originalFetch = globalThis.fetch.bind(globalThis);
  globalThis.fetch = async (input, init) => {
    const startedAt = Date.now();
    const requestId = `fetch-${++fetchRequestCounter}`;
    const method = resolveFetchMethod(input, init);
    const url = resolveFetchUrl(input);
    const timeoutController = typeof AbortController !== "undefined" ? new AbortController() : void 0;
    const signal = composeAbortSignals(init?.signal, timeoutController?.signal);
    const nextInit = signal ? { ...init ?? {}, signal } : init;
    const startEvent = {
      requestId,
      method,
      url,
      source: FETCH_SOURCE,
      startedAt,
      canAbort: Boolean(timeoutController),
      cancel: timeoutController ? () => {
        if (!timeoutController.signal.aborted) {
          timeoutController.abort(new Error("Cancelled by @kerothebosa/ui-skeleton-net timeout policy."));
        }
      } : void 0
    };
    emitFetchStart(startEvent);
    let finalized = false;
    const emitTerminalEnd = (event) => {
      if (finalized) {
        return;
      }
      finalized = true;
      emitFetchEnd(event);
    };
    try {
      const response = await originalFetch(input, nextInit);
      const endedAt = Date.now();
      emitTerminalEnd({
        requestId,
        method,
        url,
        source: FETCH_SOURCE,
        startedAt,
        endedAt,
        durationMs: endedAt - startedAt,
        ok: response.ok,
        status: response.status,
        aborted: false
      });
      return response;
    } catch (error) {
      const normalized = toError(error);
      const endedAt = Date.now();
      withSubscribers(
        fetchSubscribers,
        (subscriber) => subscriber.onError({
          requestId,
          method,
          url,
          source: FETCH_SOURCE,
          startedAt,
          error: normalized
        })
      );
      emitTerminalEnd({
        requestId,
        method,
        url,
        source: FETCH_SOURCE,
        startedAt,
        endedAt,
        durationMs: endedAt - startedAt,
        ok: false,
        status: 0,
        aborted: isAbortError(normalized)
      });
      throw error;
    }
  };
};
var cleanupFetchPatch = () => {
  if (fetchSubscribers.size > 0 || !originalFetch) {
    return;
  }
  globalThis.fetch = originalFetch;
  originalFetch = null;
};
var ensureXhrPatch = () => {
  if (originalXhrOpen || originalXhrSend || !isXmlHttpRequestAvailable()) {
    return;
  }
  originalXhrOpen = XMLHttpRequest.prototype.open;
  originalXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function(method, url, async, username, password) {
    this[XHR_META] = {
      requestId: this[XHR_META]?.requestId ?? "",
      method: method.toUpperCase(),
      url: String(url),
      startedAt: this[XHR_META]?.startedAt ?? 0,
      finalized: false
    };
    return originalXhrOpen.call(
      this,
      method,
      String(url),
      async ?? true,
      username ?? null,
      password ?? null
    );
  };
  XMLHttpRequest.prototype.send = function(body) {
    const startedAt = Date.now();
    const previousMeta = this[XHR_META];
    const requestId = `xhr-${++xhrRequestCounter}`;
    const method = previousMeta?.method ?? "GET";
    const url = previousMeta?.url ?? "";
    const startEvent = {
      requestId,
      method,
      url,
      source: XHR_SOURCE,
      startedAt,
      canAbort: true,
      cancel: () => {
        const pending = activeXhrRequests.get(requestId);
        if (pending && pending.readyState !== XMLHttpRequest.DONE) {
          pending.abort();
        }
      }
    };
    this[XHR_META] = {
      requestId,
      method,
      url,
      startedAt,
      finalized: false
    };
    activeXhrRequests.set(requestId, this);
    emitXhrStart(startEvent);
    const finalize = (status, ok, aborted) => {
      const meta = this[XHR_META];
      if (!meta || meta.finalized) {
        return;
      }
      meta.finalized = true;
      activeXhrRequests.delete(requestId);
      const endedAt = Date.now();
      emitXhrEnd({
        requestId,
        method,
        url,
        source: XHR_SOURCE,
        startedAt,
        endedAt,
        durationMs: endedAt - startedAt,
        ok,
        status,
        aborted
      });
    };
    this.addEventListener(
      "error",
      () => {
        withSubscribers(
          xhrSubscribers,
          (subscriber) => subscriber.onError({
            requestId,
            method,
            url,
            source: XHR_SOURCE,
            startedAt,
            error: new Error(`XHR error for ${method} ${url}`)
          })
        );
      },
      { once: true }
    );
    this.addEventListener(
      "abort",
      () => {
        withSubscribers(
          xhrSubscribers,
          (subscriber) => subscriber.onError({
            requestId,
            method,
            url,
            source: XHR_SOURCE,
            startedAt,
            error: new Error(`XHR aborted for ${method} ${url}`)
          })
        );
      },
      { once: true }
    );
    this.addEventListener(
      "timeout",
      () => {
        withSubscribers(
          xhrSubscribers,
          (subscriber) => subscriber.onError({
            requestId,
            method,
            url,
            source: XHR_SOURCE,
            startedAt,
            error: new Error(`XHR timed out for ${method} ${url}`)
          })
        );
      },
      { once: true }
    );
    this.addEventListener(
      "loadend",
      () => {
        const aborted = this.readyState === XMLHttpRequest.DONE && this.status === 0;
        const ok = this.status >= 200 && this.status < 400;
        finalize(this.status, ok, aborted);
      },
      { once: true }
    );
    return originalXhrSend.call(this, body);
  };
};
var cleanupXhrPatch = () => {
  if (xhrSubscribers.size > 0 || !originalXhrOpen || !originalXhrSend) {
    return;
  }
  XMLHttpRequest.prototype.open = originalXhrOpen;
  XMLHttpRequest.prototype.send = originalXhrSend;
  originalXhrOpen = null;
  originalXhrSend = null;
  activeXhrRequests.clear();
};
var registerFetchSubscriber = (subscriber) => {
  const id = ++subscriberIdCounter;
  fetchSubscribers.set(id, subscriber);
  ensureFetchPatch();
  return () => {
    fetchSubscribers.delete(id);
    cleanupFetchPatch();
  };
};
var registerXhrSubscriber = (subscriber) => {
  const id = ++subscriberIdCounter;
  xhrSubscribers.set(id, subscriber);
  ensureXhrPatch();
  return () => {
    xhrSubscribers.delete(id);
    cleanupXhrPatch();
  };
};

// src/network/fetch-interceptor.ts
var FetchInterceptor = class {
  constructor(subscriber) {
    this.subscriber = subscriber;
    this.name = "fetch";
    this.installed = false;
    this.unsubscribe = null;
  }
  install() {
    if (this.installed) {
      return;
    }
    this.unsubscribe = registerFetchSubscriber(this.subscriber);
    this.installed = true;
  }
  uninstall() {
    if (!this.installed) {
      return;
    }
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.installed = false;
  }
  isInstalled() {
    return this.installed;
  }
};

// src/network/xhr-interceptor.ts
var XhrInterceptor = class {
  constructor(subscriber) {
    this.subscriber = subscriber;
    this.name = "xhr";
    this.installed = false;
    this.unsubscribe = null;
  }
  install() {
    if (this.installed) {
      return;
    }
    this.unsubscribe = registerXhrSubscriber(this.subscriber);
    this.installed = true;
  }
  uninstall() {
    if (!this.installed) {
      return;
    }
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.installed = false;
  }
  isInstalled() {
    return this.installed;
  }
};

// src/orchestrator/event-bus.ts
var EventBus = class {
  constructor() {
    this.listeners = {};
  }
  on(event, handler) {
    if (!this.listeners[event]) {
      this.listeners[event] = /* @__PURE__ */ new Set();
    }
    this.listeners[event]?.add(handler);
  }
  off(event, handler) {
    this.listeners[event]?.delete(handler);
  }
  emit(event, payload) {
    this.listeners[event]?.forEach((handler) => {
      handler(payload);
    });
  }
  removeAll() {
    Object.values(this.listeners).forEach((listenerSet) => listenerSet?.clear());
    this.listeners = {};
  }
};

// src/utils/logger.ts
var Logger = class {
  constructor(debugEnabled) {
    this.debugEnabled = debugEnabled;
  }
  debug(...args) {
    if (!this.debugEnabled) {
      return;
    }
    console.info("[@kerothebosa/ui-skeleton-net]", ...args);
  }
  warn(...args) {
    console.warn("[@kerothebosa/ui-skeleton-net]", ...args);
  }
  error(...args) {
    console.error("[@kerothebosa/ui-skeleton-net]", ...args);
  }
};

// src/core/lifecycle.ts
var LifecycleState = {
  IDLE: "idle",
  RUNNING: "running",
  STOPPED: "stopped",
  DESTROYED: "destroyed"
};

// src/core/enhancer.ts
var DEFAULT_SKELETON_VISUALS = {
  mode: "hybrid",
  animation: "shimmer",
  theme: "classic",
  adaptive: {
    maxDepth: 4,
    maxPlaceholders: 72,
    minBlockHeightPx: 8,
    lineGapPx: 5,
    ignoreSelectors: [
      "[data-skeleton-node]",
      "script",
      "style",
      "noscript",
      "template",
      "iframe",
      "canvas"
    ]
  }
};
var DEFAULT_OPTIONS = {
  skeletonSelector: "body",
  skeletonClassName: "sknet-skeleton-active",
  overlayClassName: "sknet-skeleton-overlay",
  requestTimeoutMs: 15e3,
  timeoutMode: "abort",
  showDelayMs: 120,
  minVisibleMs: 180,
  enabledInterceptors: ["fetch", "xhr"],
  skeletonVisuals: DEFAULT_SKELETON_VISUALS,
  debug: false
};
var resolveSkeletonVisuals = (visuals) => {
  return {
    mode: visuals?.mode ?? DEFAULT_SKELETON_VISUALS.mode,
    animation: visuals?.animation ?? DEFAULT_SKELETON_VISUALS.animation,
    theme: visuals?.theme ?? DEFAULT_SKELETON_VISUALS.theme,
    adaptive: {
      maxDepth: visuals?.adaptive?.maxDepth ?? DEFAULT_SKELETON_VISUALS.adaptive.maxDepth,
      maxPlaceholders: visuals?.adaptive?.maxPlaceholders ?? DEFAULT_SKELETON_VISUALS.adaptive.maxPlaceholders,
      minBlockHeightPx: visuals?.adaptive?.minBlockHeightPx ?? DEFAULT_SKELETON_VISUALS.adaptive.minBlockHeightPx,
      lineGapPx: visuals?.adaptive?.lineGapPx ?? DEFAULT_SKELETON_VISUALS.adaptive.lineGapPx,
      ignoreSelectors: visuals?.adaptive?.ignoreSelectors ?? DEFAULT_SKELETON_VISUALS.adaptive.ignoreSelectors
    }
  };
};
var SkeletonEnhancer = class {
  constructor(options = {}) {
    this.state = LifecycleState.IDLE;
    this.bus = new EventBus();
    this.interceptors = /* @__PURE__ */ new Map();
    this.activeRequests = /* @__PURE__ */ new Map();
    this.options = {
      ...DEFAULT_OPTIONS,
      ...options,
      skeletonVisuals: resolveSkeletonVisuals(options.skeletonVisuals),
      enabledInterceptors: options.enabledInterceptors ? [...options.enabledInterceptors] : [...DEFAULT_OPTIONS.enabledInterceptors]
    };
    this.logger = new Logger(this.options.debug);
    this.skeletonManager = new SkeletonManager({
      selector: this.options.skeletonSelector,
      className: this.options.skeletonClassName,
      overlayClassName: this.options.overlayClassName,
      visuals: this.options.skeletonVisuals
    });
    this.networkSubscriber = {
      onRequestStart: (event) => this.handleNetworkRequestStart(event),
      onRequestEnd: (event) => this.handleNetworkRequestEnd(event),
      onError: (event) => this.handleNetworkError(event)
    };
    this.registerInterceptor(new FetchInterceptor(this.networkSubscriber));
    this.registerInterceptor(new XhrInterceptor(this.networkSubscriber));
    this.attachHookHandlers();
  }
  start() {
    if (this.state === LifecycleState.DESTROYED) {
      throw new Error("SkeletonEnhancer has been destroyed and cannot be started.");
    }
    if (this.state === LifecycleState.RUNNING) {
      return;
    }
    if (!isBrowserEnvironment()) {
      this.logger.warn(
        "SkeletonEnhancer.start() skipped because no browser environment was detected."
      );
      return;
    }
    this.interceptors.forEach((interceptor, name) => {
      if (!this.shouldInstallInterceptor(name)) {
        return;
      }
      interceptor.install();
    });
    this.state = LifecycleState.RUNNING;
    this.logger.debug("Enhancer started");
  }
  stop() {
    if (this.state !== LifecycleState.RUNNING) {
      return;
    }
    this.interceptors.forEach((interceptor) => interceptor.uninstall());
    this.clearRequestTracking();
    this.state = LifecycleState.STOPPED;
    this.logger.debug("Enhancer stopped");
  }
  destroy() {
    if (this.state === LifecycleState.DESTROYED) {
      return;
    }
    this.stop();
    this.bus.removeAll();
    this.clearRequestTracking();
    this.skeletonManager.dispose();
    this.state = LifecycleState.DESTROYED;
    this.logger.debug("Enhancer destroyed");
  }
  on(event, handler) {
    this.bus.on(event, handler);
    return this;
  }
  off(event, handler) {
    this.bus.off(event, handler);
    return this;
  }
  registerInterceptor(interceptor) {
    if (this.interceptors.has(interceptor.name)) {
      this.logger.warn(
        `Interceptor "${interceptor.name}" is already registered; ignoring duplicate.`
      );
      return this;
    }
    this.interceptors.set(interceptor.name, interceptor);
    if (this.state === LifecycleState.RUNNING && this.shouldInstallInterceptor(interceptor.name)) {
      interceptor.install();
    }
    return this;
  }
  unregisterInterceptor(name) {
    const interceptor = this.interceptors.get(name);
    if (!interceptor) {
      return this;
    }
    interceptor.uninstall();
    this.interceptors.delete(name);
    return this;
  }
  isRunning() {
    return this.state === LifecycleState.RUNNING;
  }
  getState() {
    return this.state;
  }
  shouldInstallInterceptor(name) {
    if (name === "fetch" || name === "xhr") {
      return this.options.enabledInterceptors.includes(name);
    }
    return true;
  }
  handleNetworkRequestStart(event) {
    if (this.state !== LifecycleState.RUNNING || !this.shouldHandleRequest(event)) {
      return;
    }
    if (this.activeRequests.has(event.requestId)) {
      return;
    }
    const tracker = {
      requestId: event.requestId,
      url: event.url,
      method: event.method,
      source: event.source,
      startedAt: event.startedAt,
      visible: false,
      shownAt: null,
      completed: false,
      timedOut: false,
      canAbort: event.canAbort ?? false,
      cancel: event.cancel ?? null,
      showTimer: null,
      hideTimer: null,
      timeoutTimer: null,
      timeoutFallbackTimer: null
    };
    this.activeRequests.set(event.requestId, tracker);
    this.bus.emit("request:start", event);
    this.scheduleShow(tracker);
    this.scheduleTimeout(tracker);
  }
  handleNetworkRequestEnd(event) {
    const tracker = this.activeRequests.get(event.requestId);
    if (!tracker || tracker.completed) {
      return;
    }
    tracker.completed = true;
    this.clearTimer(tracker.showTimer);
    this.clearTimer(tracker.timeoutTimer);
    this.clearTimer(tracker.timeoutFallbackTimer);
    tracker.showTimer = null;
    tracker.timeoutTimer = null;
    tracker.timeoutFallbackTimer = null;
    this.bus.emit("request:end", event);
    if (!tracker.visible) {
      this.activeRequests.delete(tracker.requestId);
      return;
    }
    const visibleFor = tracker.shownAt ? Date.now() - tracker.shownAt : 0;
    const hideDelay = Math.max(0, this.options.minVisibleMs - visibleFor);
    if (hideDelay === 0) {
      this.hideSkeleton(tracker.requestId);
      return;
    }
    tracker.hideTimer = setTimeout(() => {
      tracker.hideTimer = null;
      this.hideSkeleton(tracker.requestId);
    }, hideDelay);
  }
  handleNetworkError(event) {
    const tracker = this.activeRequests.get(event.requestId);
    if (!tracker || tracker.completed) {
      return;
    }
    if (tracker.timedOut) {
      return;
    }
    this.bus.emit("error", {
      requestId: event.requestId,
      url: event.url,
      method: event.method,
      source: event.source,
      error: event.error
    });
  }
  scheduleShow(tracker) {
    if (this.options.showDelayMs <= 0) {
      this.showSkeleton(tracker.requestId);
      return;
    }
    tracker.showTimer = setTimeout(() => {
      tracker.showTimer = null;
      this.showSkeleton(tracker.requestId);
    }, this.options.showDelayMs);
  }
  scheduleTimeout(tracker) {
    if (this.options.requestTimeoutMs <= 0) {
      return;
    }
    tracker.timeoutTimer = setTimeout(() => {
      tracker.timeoutTimer = null;
      this.handleTimeout(tracker.requestId);
    }, this.options.requestTimeoutMs);
  }
  showSkeleton(requestId) {
    const tracker = this.activeRequests.get(requestId);
    if (!tracker || tracker.completed || tracker.visible) {
      return;
    }
    tracker.visible = true;
    tracker.shownAt = Date.now();
    this.skeletonManager.show(requestId);
    this.bus.emit("skeleton:show", {
      requestId,
      target: this.skeletonManager.getTarget()
    });
  }
  hideSkeleton(requestId) {
    const tracker = this.activeRequests.get(requestId);
    if (!tracker) {
      return;
    }
    if (tracker.visible) {
      this.skeletonManager.hide(requestId);
      this.bus.emit("skeleton:hide", {
        requestId,
        target: this.skeletonManager.getTarget()
      });
    }
    this.clearTimer(tracker.showTimer);
    this.clearTimer(tracker.timeoutTimer);
    this.clearTimer(tracker.hideTimer);
    this.clearTimer(tracker.timeoutFallbackTimer);
    this.activeRequests.delete(requestId);
  }
  handleTimeout(requestId) {
    const tracker = this.activeRequests.get(requestId);
    if (!tracker || tracker.completed || tracker.timedOut) {
      return;
    }
    tracker.timedOut = true;
    const error = new Error(`Request timed out after ${this.options.requestTimeoutMs}ms`);
    this.bus.emit("error", {
      requestId,
      url: tracker.url,
      method: tracker.method,
      source: tracker.source,
      error
    });
    if (this.options.timeoutMode === "abort" && tracker.canAbort && tracker.cancel) {
      tracker.cancel();
      tracker.timeoutFallbackTimer = setTimeout(() => {
        tracker.timeoutFallbackTimer = null;
        this.finalizeTimedOutRequest(requestId, true);
      }, 150);
      return;
    }
    this.finalizeTimedOutRequest(requestId, false);
  }
  finalizeTimedOutRequest(requestId, aborted) {
    const tracker = this.activeRequests.get(requestId);
    if (!tracker || tracker.completed) {
      return;
    }
    const endedAt = Date.now();
    this.handleNetworkRequestEnd({
      requestId,
      url: tracker.url,
      method: tracker.method,
      source: tracker.source,
      startedAt: tracker.startedAt,
      endedAt,
      durationMs: endedAt - tracker.startedAt,
      ok: false,
      status: 0,
      aborted
    });
  }
  shouldHandleRequest(event) {
    if (!this.options.shouldHandleRequest) {
      return true;
    }
    try {
      return this.options.shouldHandleRequest({
        url: event.url,
        method: event.method,
        source: event.source
      });
    } catch (error) {
      this.logger.warn("shouldHandleRequest callback failed; defaulting to handle request.", error);
      return true;
    }
  }
  clearRequestTracking() {
    this.activeRequests.forEach((tracker) => {
      this.clearTimer(tracker.showTimer);
      this.clearTimer(tracker.hideTimer);
      this.clearTimer(tracker.timeoutTimer);
      this.clearTimer(tracker.timeoutFallbackTimer);
    });
    this.activeRequests.clear();
    this.skeletonManager.cleanup();
  }
  clearTimer(timer) {
    if (timer) {
      clearTimeout(timer);
    }
  }
  attachHookHandlers() {
    if (this.options.hooks?.onRequestStart) {
      this.on("request:start", this.options.hooks.onRequestStart);
    }
    if (this.options.hooks?.onRequestEnd) {
      this.on("request:end", this.options.hooks.onRequestEnd);
    }
    if (this.options.hooks?.onSkeletonShow) {
      this.on("skeleton:show", this.options.hooks.onSkeletonShow);
    }
    if (this.options.hooks?.onSkeletonHide) {
      this.on("skeleton:hide", this.options.hooks.onSkeletonHide);
    }
    if (this.options.hooks?.onError) {
      this.on("error", this.options.hooks.onError);
    }
  }
};

// src/dom/mutation-observer.ts
var DomMutationObserver = class {
  constructor() {
    this.observer = null;
  }
  start(observerOptions, callback) {
    this.stop();
    this.observer = new MutationObserver(callback);
    this.observer.observe(observerOptions.target, observerOptions.options ?? { childList: true });
    return this.observer;
  }
  stop() {
    this.observer?.disconnect();
    this.observer = null;
  }
};
export {
  DomMutationObserver,
  EventBus,
  FetchInterceptor,
  LifecycleState,
  SkeletonEnhancer,
  SkeletonManager,
  XhrInterceptor
};
//# sourceMappingURL=index.js.map